import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { clearTestEvents, clearExistingEvents } from '../../utils/migrateEvents';

const EventCleanup = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleClearTestEvents = async () => {
    if (!isAdmin()) {
      setMessage('❌ Admin access required');
      return;
    }

    if (!window.confirm('Are you sure you want to delete all test events (2025-2026)? This cannot be undone.')) {
      return;
    }

    setLoading(true);
    setMessage('🗑️ Clearing test events...');

    try {
      const deletedCount = await clearTestEvents();
      setMessage(`✅ Successfully deleted ${deletedCount} test events!`);
    } catch (error) {
      console.error('Error clearing test events:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllEvents = async () => {
    if (!isAdmin()) {
      setMessage('❌ Admin access required');
      return;
    }

    if (!window.confirm('⚠️ WARNING: This will delete ALL events including past events. Are you absolutely sure?')) {
      return;
    }

    setLoading(true);
    setMessage('🗑️ Clearing all events...');

    try {
      const deletedCount = await clearExistingEvents();
      setMessage(`✅ Successfully deleted ${deletedCount} events!`);
    } catch (error) {
      console.error('Error clearing all events:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p>You need admin privileges to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Event Cleanup Tool</h1>
          
          {message && (
            <div className={`p-4 rounded-lg mb-6 ${
              message.includes('✅') ? 'bg-green-50 text-green-800' : 
              message.includes('❌') ? 'bg-red-50 text-red-800' : 
              'bg-blue-50 text-blue-800'
            }`}>
              {message}
            </div>
          )}

          <div className="space-y-6">
            <div className="border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Clear Test Events</h2>
              <p className="text-gray-600 mb-4">
                Remove all test events from 2025-2026. This will keep all real past events from 2022-2023.
              </p>
              <button
                onClick={handleClearTestEvents}
                disabled={loading}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {loading ? 'Clearing...' : 'Clear Test Events'}
              </button>
            </div>

            <div className="border border-red-200 rounded-lg p-6 bg-red-50">
              <h2 className="text-lg font-semibold text-red-900 mb-3">⚠️ Clear All Events</h2>
              <p className="text-red-700 mb-4">
                <strong>DANGER:</strong> This will delete ALL events including past events. Only use this for a complete reset.
              </p>
              <button
                onClick={handleClearAllEvents}
                disabled={loading}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Clearing...' : 'Clear All Events'}
              </button>
            </div>
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">Instructions:</h3>
            <ol className="list-decimal list-inside text-blue-800 space-y-1">
              <li>Use "Clear Test Events" to remove only the 2025-2026 placeholder events</li>
              <li>This will keep all real past events from the original APN website</li>
              <li>After clearing, you can add new real upcoming events through the admin interface</li>
              <li>Only use "Clear All Events" if you want to start completely fresh</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCleanup;
